import Signal from '../control-flow/signal';
import Tool from './tool';
import logging from '../logging';
import { Camera, OrthographicCamera, PerspectiveCamera } from 'three';
import AnimationLoop from '../control-flow/animation-loop';
import { registerListener } from '../html/register-listener';
import isFreeKeyPress from '../html/is-free-keypress';

const log = logging('tools');

/**
 * Based on the DEFAULT_WALKING_SPEED in first-person-tool.ts
 */
const DEFAULT_MOVEMENT_SPEED = 70.4;

/**
 * When the shift key is depressed, how much faster do the navigation keys
 * act?
 */
const SHIFT_KEY_TIME_SCALE = 2.5;

/**
 * When the slash key (immediately to the left of the right shift key), the
 * keys act much more slowly.
 */
const SLASH_KEY_TIME_SCALE = 0.125;

const BASE_WHEEL_SPEED = 0.05;

export class Overhead3DMovementTool extends Tool {
    camera: Camera;
    canvas: HTMLCanvasElement;

    speed: number = DEFAULT_MOVEMENT_SPEED;

    onChange = new Signal('onChange', 'tools');
    
    // the timestamp of the last update
    ts: number;

    // The XY position of the pointer over the canvas in CSS pixels at the
    // moment the pointerdown event occurred
    startX: number;
    startY: number;

    // The position of the camera at the start of a mouse movement
    startCamX: number;
    startCamZ: number;

    isPointerDown: boolean = false;
    keysDown: Set<string> = new Set();

    loop = new AnimationLoop(() => {
        const
            now = performance.now(),
            delta = (now - this.ts) / 1000;
        this.ts = now;

        if(this.isPointerDown || this.keysDown.size > 0) {
            this.update(delta);
        }
    });

    constructor(camera: Camera, canvas: HTMLCanvasElement) {
        super();
        log.debug('Overhead3DMovementTool constructor()');
        this.camera = camera;
        this.canvas = canvas;

    }

    enable(): void {
        log.debug('Overhead3DMovementTool.enable()');

        this.ts = performance.now();
        this.loop.start();

        registerListener(this.canvas, 'pointerdown', this.begin);
        registerListener(this.canvas, 'wheel', this.wheel);

        registerListener(window, 'keydown', this.keyDown);
        registerListener(window, 'keyup', this.keyUp);
    }

    disable(): void {
        log.debug('Overhead3DMovementTool.disable()');

        this.loop.stop();

        this.canvas.removeEventListener('pointerdown', this.begin);
        this.canvas.removeEventListener('wheel', this.wheel);
        removeEventListener('pointerup', this.end);
        removeEventListener('pointermove', this.move);
        removeEventListener('keydown', this.keyDown);
        removeEventListener('keyup', this.keyUp);
    }

    begin(event: PointerEvent) {
        log.debug('Overhead3DMovementTool.begin()');

        registerListener(window, 'pointerup', this.end);
        registerListener(window, 'pointermove', this.move);

        this.isPointerDown = true;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.startCamX = this.camera.position.x;
        this.startCamZ = this.camera.position.z;
    }

    wheel = (event: WheelEvent) => {
        let speed = BASE_WHEEL_SPEED;
        if(this.keysDown.has('shift')) {
            speed *= SHIFT_KEY_TIME_SCALE;
        } else if(this.keysDown.has('/')) {
            speed *= SLASH_KEY_TIME_SCALE;
        }

        if(event.deltaY != 0) {
            this.adjustElevation(event.deltaY * speed);
            this.onChange.trigger();
        }
    }

    move = (event: PointerEvent) => {
        const
            dx = event.clientX - this.startX,
            dy = event.clientY - this.startY;

        let scale: number = 1; // inches per CSS pixel
        if(this.camera instanceof PerspectiveCamera) {
            // Solve for the span of floor covered by the vertical field of
            // view and divide by the vertical size of the canvas. Given d is
            // the distance from the camera to the floor, f is the vertical
            // field of view of the camera, and h is the total span of floor in
            // the field of view, then `tan(f/2)` is equal to the opposite over
            // adjacent sides, i.e. h/2 over d, or h/2d. If we solve for h, we
            // get h = 2*d*tan(f/2).
            const
                pixels = this.canvas.clientHeight,
                elevation = this.camera.position.y,
                // the _vertical_ fov
                fov = Math.PI * this.camera.fov / 180,
                inches = 2 * elevation * Math.tan(fov/2);
            scale = inches / pixels;
        }
        else if(this.camera instanceof OrthographicCamera) {
            const
                pixels = this.canvas.clientWidth,
                inches = this.camera.right - this.camera.left;
            scale = inches / pixels;
        }

        this.camera.position.x = this.startCamX - dx * scale;
        this.camera.position.z = this.startCamZ - dy * scale;
    }

    cancel(): void {
        // do nothing
    }

    end(event: PointerEvent) {
        log.debug('Overhead3DMovementTool.end()');
        removeEventListener('pointermove', this.move);
        this.isPointerDown = false;
    }

    keyDown = (event: KeyboardEvent) => {
        if(ignoreKey(event)) return;
        this.keysDown.add(event.key.toLowerCase());
    }

    keyUp = (event: KeyboardEvent) => {
        this.keysDown.delete(event.key.toLowerCase());
    }

    /**
     * @param delta - the time elapsed in seconds since the last frame,
     * usually approximately 1/30 seconds, or whatever the framerate is.
     */
    update = (delta: number) => {
        this.onChange.trigger();

        if(this.keysDown.has('shift')) {
            delta *= SHIFT_KEY_TIME_SCALE;
        } else if(this.keysDown.has('/')) {
            delta *= SLASH_KEY_TIME_SCALE;
        }

        for(const key of this.keysDown) {
            switch(key) {
                case 'a':
                case 'arrowleft':
                    this.camera.position.x -= this.speed * delta;
                    break;
                case 'arrowup':
                case 'w':
                    this.camera.position.z -= this.speed * delta;
                    break;
                case 'arrowdown':
                case 's':
                    this.camera.position.z += this.speed * delta;
                    break;
                case 'd':
                case 'arrowright':
                    this.camera.position.x += this.speed * delta;
                    break;
                case 'z':
                    this.adjustElevation(-this.speed * delta)
                    break;
                case 'x':
                    this.adjustElevation(this.speed * delta)
                    break;
            }
        }
    }

    adjustElevation(dy: number) {
        if(this.camera instanceof PerspectiveCamera) {
            this.camera.position.y = Math.max(
                0,
                this.camera.position.y + dy);
        } else if(this.camera instanceof OrthographicCamera) {
            const
                width = this.camera.right - this.camera.left,
                height = this.camera.bottom - this.camera.top,
                aspect = width / height,
                y = dy/2,
                x = aspect * y;
            this.camera.top += y;
            this.camera.bottom -= y;
            this.camera.right -= x;
            this.camera.left += x;
            this.camera.updateProjectionMatrix();
        }
    }
}
export default Overhead3DMovementTool;

const ignoreKey = (event: KeyboardEvent): boolean => {
    // We don't want to capture events from input sources
    if(!isFreeKeyPress(event)) return true;

    // If a modifier key (except shift) is pressed, we'll ignore that too
    if(event.altKey || event.ctrlKey || event.metaKey) {
        return true;
    }
    return false;
}
